/* ============================================================================
   /portal/auth/verify-email - reads ?token=XXX from hash, calls verifyEmail.
   ----------------------------------------------------------------------------
   Reskinned to match the marketing welcome panel: hero gradient + neon green
   Space Grotesk headline. Verify behaviour preserved (loading, success,
   error, no-token states).
   ========================================================================= */
import { useEffect, useState } from "react";
import { useLocation, Link } from "wouter";
import { useAuth } from "@/components/AuthProvider";
import { Logo } from "@/components/Logo";
import { ChevronRight, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

function useHashQuery() {
  // Extract query params from the hash portion of the URL (#/path?key=value).
  const [, ] = useLocation();
  const hash = window.location.hash;
  const queryStart = hash.indexOf("?");
  if (queryStart < 0) return new URLSearchParams();
  return new URLSearchParams(hash.slice(queryStart + 1));
}

export default function AuthVerifyEmail() {
  const { verifyEmail } = useAuth();
  const params = useHashQuery();
  const token = params.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error" | "no-token">(
    token ? "loading" : "no-token"
  );
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      const result = await verifyEmail(token);
      if (result.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setMessage(result.message ?? "Verification failed.");
      }
    })();
  }, [token]);

  return (
    <div
      className="relative min-h-screen text-foreground flex justify-center px-4 auth-shell-safe-top pb-12 sm:items-center"
      style={{ background: "hsl(230 35% 4%)" }}
    >
      {/* Aurora glows */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: "-80px",
          right: "-60px",
          width: 360,
          height: 360,
          background: "radial-gradient(circle, hsl(142 78% 56% / 0.18), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          bottom: "-60px",
          left: "-80px",
          width: 320,
          height: 320,
          background: "radial-gradient(circle, hsl(174 60% 53% / 0.10), transparent 60%)",
        }}
      />

      <div
        className="relative w-full max-w-sm v2-neon-card p-7 text-center"
        data-testid="card-verify-email"
      >
        <div className="flex justify-center mb-5">
          <Logo size={48} />
        </div>

        {status === "loading" && (
          <>
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{
                background: "hsl(142 78% 28% / 0.4)",
                border: "1.5px solid hsl(142 78% 56% / 0.45)",
              }}
            >
              <Loader2
                className="w-6 h-6 animate-spin"
                style={{ color: "hsl(142 78% 70%)" }}
              />
            </div>
            <div className="flex justify-center">
              <span className="v2-eyebrow" data-testid="badge-verify-loading-eyebrow">
                <span className="v2-eyebrow__dot" />
                Verify
              </span>
            </div>
            <h1
              className="v2-display mt-4 text-2xl leading-tight"
              style={{ color: "var(--text-ink-bright)" }}
            >
              Checking your link...
            </h1>
            <p
              className="text-xs mt-2 leading-relaxed"
              style={{ color: "var(--text-ink-faint)" }}
            >
              Just a moment.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{
                background: "hsl(142 78% 28% / 0.4)",
                border: "1.5px solid hsl(142 78% 56% / 0.55)",
                boxShadow: "0 0 24px hsl(142 100% 50% / 0.3)",
              }}
            >
              <CheckCircle2 className="w-6 h-6" style={{ color: "hsl(142 78% 70%)" }} />
            </div>
            <div className="flex justify-center">
              <span className="v2-eyebrow" data-testid="badge-verify-success-eyebrow">
                <span className="v2-eyebrow__dot" />
                Verified
              </span>
            </div>
            <h1
              className="v2-display mt-4 text-2xl leading-tight"
              style={{ color: "var(--text-ink-bright)" }}
            >
              Email{" "}
              <span style={{ color: "hsl(var(--primary))" }}>confirmed.</span>
            </h1>
            <p
              className="text-xs mt-2 leading-relaxed"
              style={{ color: "var(--text-ink-faint)" }}
            >
              Your email address is locked in. You are all set.
            </p>
            <Link
              href="/"
              className="v2-neon-cta mt-6 w-full inline-flex items-center justify-center gap-2 py-3 text-sm rounded-full no-underline"
              data-testid="button-go-portal"
            >
              Open my Dex
              <ChevronRight className="w-4 h-4" />
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{
                background: "hsl(0 70% 30% / 0.35)",
                border: "1.5px solid hsl(0 70% 60% / 0.55)",
              }}
            >
              <AlertTriangle className="w-6 h-6" style={{ color: "hsl(0 80% 75%)" }} />
            </div>
            <h1
              className="v2-display text-2xl leading-tight"
              style={{ color: "hsl(0 80% 78%)" }}
            >
              Could not verify
            </h1>
            <p
              className="text-xs mt-2 leading-relaxed"
              style={{ color: "var(--text-ink-faint)" }}
            >
              {message}
            </p>
            <Link
              href="/portal/account"
              className="v2-neon-cta mt-5 w-full inline-flex items-center justify-center gap-2 py-3 text-sm rounded-full no-underline"
              data-testid="button-resend"
            >
              Request a new link
              <ChevronRight className="w-4 h-4" />
            </Link>
          </>
        )}

        {status === "no-token" && (
          <>
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{
                background: "hsl(0 70% 30% / 0.35)",
                border: "1.5px solid hsl(0 70% 60% / 0.55)",
              }}
            >
              <AlertTriangle className="w-6 h-6" style={{ color: "hsl(0 80% 75%)" }} />
            </div>
            <h1
              className="v2-display text-2xl leading-tight"
              style={{ color: "var(--text-ink-bright)" }}
            >
              Invalid link
            </h1>
            <p
              className="text-xs mt-2 leading-relaxed"
              style={{ color: "var(--text-ink-faint)" }}
            >
              This verification link is missing a token. Please use the link
              from your email.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
